import {
    createContext,
    Dispatch,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import type { RequestResult } from './api/v2/client';

export type ApiResponse<T> = {
    status: number;
    errors?: string[];
    result: T;
};

export type ApiErrorSingle = string | Response;
export type ApiV2ErrorShape = Array<{
    message: string;
    location: string;
    value: string | number | boolean | null;
    code: string;
}>;
export type ApiError = ApiErrorSingle | ApiErrorSingle[] | ApiV2ErrorShape;

let accessToken: string | null = null;

export function setAccessToken(t: string) {
    accessToken = t;
}

const baseUrl = '/api';
const apiBaseUrl = `${baseUrl}/v1`;
export const unauthorized = 'ERR_UNAUTHORIZED';
export const notFound = 'ERR_NOT_FOUND';

export type ApiCallOptions = {
    root?: boolean;
    headers?: Record<string, string>;
    includeToken?: boolean;
    body?: any;
} & Omit<RequestInit, 'body'>;

async function rawCall(url: string, options: ApiCallOptions = {}): Promise<Response> {
    if (!options.headers) {
        options.headers = {};
    }
    if (accessToken) {
        options.headers.Authorization = `Bearer ${accessToken}`;
    }
    options.headers['X-APP-PATH'] = window.location.pathname;
    options.headers['X-APP-QUERY'] = window.location.search;
    const res = await fetch(apiBaseUrl + url, options);

    if (res.status === 401) {
        throw unauthorized;
    } else if (res.status === 404) {
        throw notFound;
    } else if (!res.ok) {
        try {
            const result = (await res.json()) as ApiResponse<any>;
            throw result.errors || res.statusText;
        } catch (e) {
            console.error(e);
            throw res;
        }
    }

    return res;
}

async function apiCall<T>(url: string, options: ApiCallOptions = {}): Promise<T> {
    const { body, headers = {}, ...fetchOptions } = options;
    if (options.includeToken && accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
    }
    const opts = {
        ...fetchOptions,
        headers: {
            ...headers,
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-APP-PATH': window.location.pathname,
            'X-APP-QUERY': window.location.search,
        },
        body: body ? JSON.stringify(body) : undefined,
    };

    const res = await fetch(url, opts);

    try {
        const result = (await res.json()) as ApiResponse<T>;

        if (!res.ok) {
            throw result.errors || res;
        }

        return result.result;
    } catch (e) {
        if (res.status === 401) {
            throw unauthorized;
        }

        throw Array.isArray(e) ? e : res;
    }
}

export async function api<T>(url: string, options: ApiCallOptions = {}): Promise<T> {
    const { root, body, headers = {}, ...fetchOptions } = options;

    if (accessToken) {
        headers['Authorization'] = `Bearer ${accessToken}`;
    }

    if (body instanceof FormData) {
        delete headers['Content-Type'];
    } else if (!headers['Content-Type']) {
        headers['Content-Type'] = 'application/json';
    }

    const opts = {
        ...fetchOptions,
        headers: {
            ...headers,
            Accept: 'application/json',
            'X-APP-PATH': window.location.pathname,
            'X-APP-QUERY': window.location.search,
        },
        body: body ? (body instanceof FormData ? body : JSON.stringify(body)) : undefined,
    };

    const u = root ? url : apiBaseUrl + url;
    const res = await fetch(u, opts);

    try {
        const result = await res.json();
        if (!res.ok) {
            let errors = result.errors || [res.statusText];
            if (res.status === 401) {
                errors = [unauthorized, ...errors];
            }
            if (res.status === 404) {
                errors = [notFound, ...errors];
            }

            throw errors;
        }

        return result.result;
    } catch (e) {
        if (Array.isArray(e)) {
            throw e;
        }

        if (res.status === 401) {
            throw unauthorized;
        }

        if (res.status === 404) {
            throw notFound;
        }

        throw res;
    }
}

api.manage = <T>(options: ApiCallOptions = {}): Promise<T> => {
    return apiCall<T>(`${baseUrl}/manage`, options);
};

api.auth = <T>(url: string, options: ApiCallOptions = {}): Promise<T> => {
    return apiCall<T>(`${baseUrl}/auth${url}`, options);
};

api.root = <T>(url: string, options: ApiCallOptions = {}) => apiCall<T>(url, options);

api.raw = rawCall;

export type ApiResource<T> = {
    loading: boolean;
    error: boolean;
    loaded: boolean;
    rawError: ApiError | null;
    hasError: (target: string) => boolean;
    data: T | null;
    setData: Dispatch<SetStateAction<T | null>>;
    refresh: (silent?: boolean) => void;
    refreshFull: () => void;
};

export type ResourceContextType = {
    fetchAnonHandlers: ((silent?: boolean) => void)[]; // anonymous handlers for backward compatibility
    fetchHandlers: Map<string, (silent?: boolean) => void>;
    addFetchHandler: (handler: (silent?: boolean) => void, id?: string) => void;
    removeFetchHandler: (handler: (silent?: boolean) => void, id?: string) => void;
    refresh: (ids: string[], silent?: boolean) => void;
    refreshAll: (silent?: boolean) => void;
};

const context: ResourceContextType = {
    fetchAnonHandlers: [], // anonymous handlers for backward compatibility
    fetchHandlers: new Map<string, (silent?: boolean) => void>(),
    addFetchHandler: (handler, id) => {
        if (id !== undefined) {
            context.fetchHandlers.set(id, handler);
        } else {
            context.fetchAnonHandlers.push(handler);
        }
    },
    removeFetchHandler: (handler, id) => {
        if (id !== undefined) {
            context.fetchHandlers.delete(id);
        } else {
            context.fetchAnonHandlers.splice(context.fetchAnonHandlers.indexOf(handler), 1);
        }
    },

    refresh: (ids: string[], silent) => {
        ids.forEach((id) => {
            const handler = context.fetchHandlers.get(id);
            if (handler) {
                handler(silent);
            } else {
                console.warn(`No resource refresh handler found for id: ${id}`);
            }
        });
    },
    refreshAll: (silent) => {
        context.fetchAnonHandlers.concat(...context.fetchHandlers.values()).forEach((handler) => {
            handler(silent);
        });
    },
};

export function useResourceRefresh(ids: string[] = [], deps: any[] = []): () => void {
    const { refresh: resourceRefresh } = useContext(ResourceContext);

    const idsKey = JSON.stringify(ids);
    const refreshHandler = useCallback(
        () => resourceRefresh(JSON.parse(idsKey) as string[]),
        [idsKey, resourceRefresh],
    );

    useEffect(() => {
        refreshHandler();
    }, [...deps]);

    return refreshHandler;
}

export const ResourceContext = createContext<ResourceContextType>(context);

/**
 * Extracts the data type from API v2 response shape { data, error?, request, response }.
 * For non-API-v2 responses (e.g. legacy api() returning data directly), returns T as-is.
 */
type ExtractDataFromResponse<T> = T extends {
    request: Request;
    response: Response;
    data?: infer D;
}
    ? Exclude<D, undefined>
    : T;

function isRequestAborted(err: unknown, signal: AbortSignal): boolean {
    if (signal.aborted) return true;
    if (typeof err === 'string' && err.startsWith('abort by code')) return true;
    if (err instanceof DOMException && err.name === 'AbortError') return true;
    if (err instanceof Error && err.name === 'AbortError') return true;
    return false;
}

export function useResource<T>(
    call: (args: { signal?: AbortSignal }) => Promise<T>,
    deps: any[] = [],
    id?: string,
): ApiResource<ExtractDataFromResponse<T>> {
    const [state, setState] = useState<string>('loading');
    const [data, setData] = useState<ExtractDataFromResponse<T> | null>(null);
    const [error, setError] = useState<ApiError | null>(null);
    const abortRef = useRef<() => void>(null);
    const resourceContext = useContext(ResourceContext);

    const request = (silent = true) => {
        const abortController = new AbortController();

        if (!silent) {
            setState('loading');
            setError(null);
        }

        // by sending an abort signal we can abort fetch requests.
        call({ signal: abortController.signal })
            .then((result) => {
                if (abortController.signal.aborted) return;

                // API v2 shape: { data, error?, request, response }
                if (
                    result != null &&
                    typeof result === 'object' &&
                    'request' in result &&
                    'response' in result
                ) {
                    const apiResult = result as {
                        data?: ExtractDataFromResponse<T>;
                        error?: unknown;
                        request: Request;
                        response: Response;
                    };
                    if (apiResult.error !== undefined) {
                        setState('error');
                        setError(apiResult.error as ApiError);
                        setData(null);
                    } else {
                        setError(null);
                        setData(apiResult.data === undefined ? null : apiResult.data);
                        setState('loaded');
                    }
                } else {
                    // Legacy API: result is the data directly
                    setError(null);
                    setData(
                        (result as ExtractDataFromResponse<T>) === undefined
                            ? null
                            : (result as ExtractDataFromResponse<T>),
                    );
                    setState('loaded');
                }
            })
            .catch((err: ApiError) => {
                if (isRequestAborted(err, abortController.signal)) return;
                console.error(err);
                setState('error');
                setError(err);
            });

        return () => abortController.abort(`abort by code: ${id ?? 'anonymous'}`);
    };

    const doFetch = (silent?: boolean) => {
        abortRef.current?.();
        abortRef.current = request(silent);
    };

    // when call changes, we update refresh handlers
    useEffect(() => {
        resourceContext.addFetchHandler(doFetch, id);

        return () => {
            resourceContext.removeFetchHandler(doFetch, id);
        };
    }, [call]);

    // refresh by dependency change
    useEffect(() => {
        doFetch(false);
    }, [...deps]);

    return {
        loading: state === 'loading',
        error: state === 'error',
        loaded: state === 'loaded',
        rawError: error,
        hasError: (target: string) => hasError(error, target),
        data,
        setData,
        refresh: doFetch,
        refreshFull: () => doFetch(false),
    };
}

// helper to be used when we want to mock an API response inside useResource when we already have the data
export type ApiV2ResponseShape<T> = Extract<
    Awaited<RequestResult<{ 200: T }, never, false, 'fields'>>,
    { error: undefined }
>;

/** API v2 error response: { error: ServerErrorPayload, request, response }. ServerErrorPayload has `errors` array. */
export type ApiV2ErrorResponseShape = {
    data?: undefined;
    error: { errors?: ApiV2ErrorShape; title?: string; status?: number; detail?: string };
    request: Request;
    response: Response;
};

/** Raw API v2 server error body (thrown when throwOnError: true). Has `errors` at top level. */
export type ApiV2ServerErrorShape = {
    title?: string;
    status?: number;
    detail?: string;
    errors?: ApiV2ErrorShape;
};

export function hasError(err: unknown, target: string): boolean {
    if (Array.isArray(err)) {
        for (const e of err) {
            if (isError(e, target)) {
                return true;
            }
        }
        return false;
    }

    if (isApiV2ServerError(err)) {
        return err.errors.some((e) => isError(e, target));
    }

    return err instanceof Response ? err.statusText === target : err === target;
}

function isApiV2ServerError(
    err: unknown,
): err is ApiV2ServerErrorShape & { errors: ApiV2ErrorShape } {
    return typeof err === 'object' && err !== null && 'errors' in err && Array.isArray(err.errors);
}

function isError(err: unknown, target: string): boolean {
    if (typeof err === 'string') {
        return err === target;
    }

    if (err instanceof Response) {
        return err.statusText === target;
    }

    return typeof err === 'object' && err !== null && 'code' in err && err.code === target;
}
